import React, { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import TextInput from '@/components/ui/inputs/TextInput'
import Pagination from '@/components/ui/pagination/Pagination'
import AddDoctorDialog from '@/containers/group/AddDoctorDialog'
import StaffsTable from '@/pages/staffs/components/StaffsTable'
import { sanitizeStaffCreation } from '@/utils/staff'

import { useSearchDoctors } from '../model/useSearchDoctors'
import { useAddDoctor } from '../model/useAddDoctor'
import { Divider } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSelectDoctorForm } from '../model/useSelectDoctorForm'

export const rowsPerPageOptions = [5]

export function SelectDoctor({ expertiseId, setActiveStep }: { expertiseId: string; setActiveStep: Dispatch<SetStateAction<number>> }) {
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)

  const { addDoctorDialogOpen, setAddDoctorDialogOpen, isAddingDoctor, addDoctor } = useAddDoctor()
  const { doctors, isSearchingDoctors, setSearchDoctorsParams, totalDoctors } = useSearchDoctors()
  const { isSendingExpertise, rhForm, onSubmit } = useSelectDoctorForm(expertiseId)
  const {
    formState: { isValid },
  } = rhForm

  return (
    <Stack height="100%">
      <Stack direction="row" alignItems="center" p={2} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <SearchDoctorInput setSearchDoctorsParams={setSearchDoctorsParams} />
        </Box>
        <Button onClick={() => setAddDoctorDialogOpen(true)} variant="contained">
          {t('page.expertise.selectDoctorStep.addDoctorButton.label')}
        </Button>
      </Stack>
      <Box sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
        <StaffsTable
          autoHightlight={false}
          loading={isSearchingDoctors}
          data={doctors}
          onSelect={(staff) => rhForm.setValue('doctorId', staff.id, { shouldValidate: true })}
          showAction={false}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          disableChangeRowsPerPage
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={(newPage: number) => {
            if (page === newPage) return
            setPage(newPage)
            setSearchDoctorsParams((prevParams) => ({ ...prevParams, options: { ...prevParams.options, skip: (newPage - 1) * rowsPerPageOptions[0] } }))
          }}
          totalItems={totalDoctors}
        />
      </Box>
      <AddDoctorDialog
        isAdding={isAddingDoctor}
        open={addDoctorDialogOpen}
        onAddDoctor={(values) => addDoctor(sanitizeStaffCreation(values))}
        onClose={() => setAddDoctorDialogOpen(false)}
      />
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3}>
        <Button onClick={() => setActiveStep((prevStep) => prevStep - 1)} variant="neutral">
          {t('common:button.back.label')}
        </Button>
        <Stack direction="row" alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
          <LoadingButton
            color="primary"
            disabled={!isValid}
            loading={isSendingExpertise}
            onClick={() => {
              rhForm.handleSubmit(onSubmit)()
            }}
            variant="contained"
          >
            {t('common:button.confirm.label')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

function SearchDoctorInput({ setSearchDoctorsParams }: { setSearchDoctorsParams: ReturnType<typeof useSearchDoctors>['setSearchDoctorsParams'] }) {
  const { t } = useTranslation()

  return (
    <TextInput
      id="search-doctor"
      fullWidth
      onChange={(event) => {
        const text = event.target.value
        setSearchDoctorsParams({ query: { name: text, surname: text, status: 'MEDECIN' }, options: { limit: 5, skip: 0 } })
      }}
      placeholder={t('page.expertise.selectDoctorStep.searchDoctor.placeholder')}
    />
  )
}
